import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  onSnapshot,
  Unsubscribe,
  Timestamp,
  serverTimestamp,
  deleteDoc 
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../lib/firebase/config';
import { notificationService } from './notificationService';
import { 
  ContentCreationSubmission, 
  ContentCreationFormData, 
  ContentReviewAction,
  ContentApprovalStatus,
  SocialPlatform 
} from '../lib/types/content-creation';

class ContentCreationService {
  private static instance: ContentCreationService;

  public static getInstance(): ContentCreationService {
    if (!ContentCreationService.instance) {
      ContentCreationService.instance = new ContentCreationService();
    }
    return ContentCreationService.instance;
  }

  // Submit new content for approval
  async submitContent(
    formData: ContentCreationFormData,
    userId: string,
    userName: string,
    userEmail: string
  ): Promise<string> {
    try {
      // Upload media files first
      const mediaUrls = await this.uploadMediaFiles(formData.mediaFiles, userId);

      // Determine media type
      const mediaType = this.determineMediaType(formData.mediaFiles);

      // Handle thumbnail upload or selection
      let thumbnailUrl: string | undefined;
      
      if (formData.thumbnailFile) {
        // Upload custom thumbnail for videos
        thumbnailUrl = await this.uploadThumbnailFile(formData.thumbnailFile, userId);
      } else if (mediaType === 'carousel' && formData.selectedThumbnailIndex !== undefined) {
        // Use selected image from carousel as thumbnail
        thumbnailUrl = mediaUrls[formData.selectedThumbnailIndex];
      } else if (mediaType === 'image') {
        // For single images, use the image itself as thumbnail
        thumbnailUrl = mediaUrls[0];
      }
      // For videos without custom thumbnail, no thumbnail URL is set

      // Create submission data
      const submissionData: Omit<ContentCreationSubmission, 'id'> = {
        submittedBy: userId,
        submittedByName: userName,
        submittedByEmail: userEmail,
        title: formData.title.trim(),
        caption: formData.caption.trim(),
        platform: formData.platform,
        mediaFiles: mediaUrls,
        mediaType,
        ...(thumbnailUrl && { thumbnailUrl }),
        ...(formData.promotedItems && formData.promotedItems.trim() && {
          promotedItems: [formData.promotedItems.trim()]
        }),
        status: 'submitted',
        submittedAt: new Date(),
        updatedAt: new Date(),
      };

      // Add to Firestore
      const docRef = await addDoc(collection(db, 'content-creation-submissions'), {
        ...submissionData,
        submittedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Send notifications to reviewers
      try {
        await notificationService.notifyContentSubmitted(
          docRef.id,
          formData.title,
          userId,
          userName,
          formData.platform
        );
      } catch (notificationError) {
        console.error('Failed to send content submission notifications:', notificationError);
        // Don't fail the submission if notifications fail
      }

      return docRef.id;
    } catch (error: any) {
      // Enhanced error handling for production
      let userFriendlyError = error;

      // File upload errors
      if (error.message?.includes('upload') || error.code?.includes('storage')) {
        if (error.code === 'storage/unauthorized') {
          userFriendlyError = new Error('File upload unauthorized. Please check storage permissions.');
        } else if (error.code === 'storage/quota-exceeded') {
          userFriendlyError = new Error('Storage quota exceeded. Please contact your administrator.');
        } else if (error.code === 'storage/retry-limit-exceeded') {
          userFriendlyError = new Error('Upload failed after multiple attempts. Please try again later.');
        } else if (error.code === 'storage/invalid-format') {
          userFriendlyError = new Error('Invalid file format. Please check your file type.');
        } else {
          userFriendlyError = new Error(error.message || 'File upload failed with unknown error');
        }
      }
      // Firestore errors
      else if (error.code === 'permission-denied') {
        userFriendlyError = new Error('Permission denied. Your account may not have permission to submit content.');
      } else if (error.code === 'failed-precondition') {
        userFriendlyError = new Error('Database indexes are still being created. Please try again in a few minutes.');
      } else if (error.code === 'unavailable') {
        userFriendlyError = new Error('Database is temporarily unavailable. Please try again later.');
      } else if (error.message?.includes('index')) {
        userFriendlyError = new Error('Database indexing in progress. Please try again in a few minutes.');
      } else if (error.code === 'deadline-exceeded') {
        userFriendlyError = new Error('Request timeout. Please try again.');
      }
      // Network errors
      else if (error.code === 'network-request-failed' || error.message?.includes('network')) {
        userFriendlyError = new Error('Network error. Please check your internet connection and try again.');
      }
      // Generic fallback
      else if (!error.message?.includes('Permission') && !error.message?.includes('Database')) {
        userFriendlyError = new Error(`Content submission failed: ${error.message || 'Unknown error occurred'}`);
      }

      throw userFriendlyError;
    }
  }

  // Upload media files to Firebase Storage
  private async uploadMediaFiles(files: File[], userId: string): Promise<string[]> {
    const uploadPromises = files.map(async (file, index) => {
      try {
        // Validate file before upload
        if (!file || file.size === 0) {
          throw new Error(`Invalid file: ${file?.name || 'Unknown file'}`);
        }

        if (file.size > 500 * 1024 * 1024) { // 500MB limit
          throw new Error(`File "${file.name}" exceeds 500MB limit`);
        }

        const timestamp = Date.now();
        const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        const path = `content-creation/${userId}/${timestamp}-${index}-${sanitizedFileName}`;
        const storageRef = ref(storage, path);
        
        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);
        
        return downloadURL;
      } catch (error: any) {
        // Enhanced error handling for file uploads
        let uploadError = error;
        
        if (error.code === 'storage/unauthorized') {
          uploadError = new Error(`Upload unauthorized for file "${file.name}". Please check storage permissions.`);
        } else if (error.code === 'storage/quota-exceeded') {
          uploadError = new Error(`Storage quota exceeded while uploading "${file.name}". Please contact your administrator.`);
        } else if (error.code === 'storage/retry-limit-exceeded') {
          uploadError = new Error(`Upload failed for "${file.name}" after multiple attempts. Please try again later.`);
        } else if (error.code === 'storage/invalid-format') {
          uploadError = new Error(`Invalid format for file "${file.name}". Please check your file type.`);
        } else if (error.code === 'storage/object-not-found') {
          uploadError = new Error(`Upload path not found for file "${file.name}". Please try again.`);
        } else if (error.code === 'storage/unknown') {
          uploadError = new Error(`Unknown storage error for file "${file.name}". Please try again.`);
        } else if (error.message?.includes('size') || error.message?.includes('limit')) {
          uploadError = new Error(`File "${file.name}" is too large. Maximum size is 500MB.`);
        } else if (!error.message?.includes('Invalid file') && !error.message?.includes('exceeds')) {
          uploadError = new Error(`Failed to upload "${file.name}": ${error.message || 'Unknown error'}`);
        }
        
        throw uploadError;
      }
    });

    try {
      const results = await Promise.all(uploadPromises);
      return results;
    } catch (error: any) {
      // If any upload fails, re-throw the specific error without adding extra prefix
      throw error;
    }
  }

  // Upload thumbnail file to Firebase Storage
  private async uploadThumbnailFile(file: File, userId: string): Promise<string> {
    try {
      // Validate file before upload
      if (!file || file.size === 0) {
        throw new Error(`Invalid thumbnail file: ${file?.name || 'Unknown file'}`);
      }

      if (!file.type.startsWith('image/')) {
        throw new Error(`Thumbnail must be an image file: ${file.name}`);
      }

      if (file.size > 10 * 1024 * 1024) { // 10MB limit for thumbnails
        throw new Error(`Thumbnail file "${file.name}" exceeds 10MB limit`);
      }

      const timestamp = Date.now();
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const path = `content-creation/${userId}/thumbnails/${timestamp}-${sanitizedFileName}`;
      const storageRef = ref(storage, path);
      
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      
      return downloadURL;
    } catch (error: any) {
      // Enhanced error handling for thumbnail uploads
      let uploadError = error;
      
      if (error.code === 'storage/unauthorized') {
        uploadError = new Error(`Thumbnail upload unauthorized for file "${file.name}". Please check storage permissions.`);
      } else if (error.code === 'storage/quota-exceeded') {
        uploadError = new Error(`Storage quota exceeded while uploading thumbnail "${file.name}". Please contact your administrator.`);
      } else if (error.code === 'storage/retry-limit-exceeded') {
        uploadError = new Error(`Thumbnail upload failed for "${file.name}" after multiple attempts. Please try again later.`);
      } else if (error.code === 'storage/invalid-format') {
        uploadError = new Error(`Invalid format for thumbnail "${file.name}". Please check your file type.`);
      } else if (error.code === 'storage/object-not-found') {
        uploadError = new Error(`Thumbnail upload path not found for file "${file.name}". Please try again.`);
      } else if (error.code === 'storage/unknown') {
        uploadError = new Error(`Unknown storage error for thumbnail "${file.name}". Please try again.`);
      } else if (error.message?.includes('size') || error.message?.includes('limit')) {
        uploadError = new Error(`Thumbnail file "${file.name}" is too large. Maximum size is 10MB.`);
      } else if (!error.message?.includes('Invalid') && !error.message?.includes('exceeds')) {
        uploadError = new Error(`Failed to upload thumbnail "${file.name}": ${error.message || 'Unknown error'}`);
      }
      
      throw uploadError;
    }
  }

  // Determine media type based on files
  private determineMediaType(files: File[]): 'image' | 'video' | 'carousel' {
    if (files.length > 1) return 'carousel';
    if (files.length === 1) {
      return files[0].type.startsWith('video/') ? 'video' : 'image';
    }
    return 'image';
  }

  // Get submissions for a specific user
  async getUserSubmissions(userId: string): Promise<ContentCreationSubmission[]> {
    try {
      const q = query(
        collection(db, 'content-creation-submissions'),
        where('submittedBy', '==', userId),
        orderBy('submittedAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        submittedAt: doc.data().submittedAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        reviewedAt: doc.data().reviewedAt?.toDate(),
      })) as ContentCreationSubmission[];
    } catch (error) {
      console.error('Error fetching user submissions:', error);
      throw error;
    }
  }

  // Get all submissions (for admins)
  async getAllSubmissions(): Promise<ContentCreationSubmission[]> {
    try {
      const q = query(
        collection(db, 'content-creation-submissions'),
        orderBy('submittedAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        submittedAt: doc.data().submittedAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        reviewedAt: doc.data().reviewedAt?.toDate(),
      })) as ContentCreationSubmission[];
    } catch (error) {
      console.error('Error fetching all submissions:', error);
      throw error;
    }
  }

  // Real-time listener for user submissions
  listenToUserSubmissions(
    userId: string,
    callback: (submissions: ContentCreationSubmission[]) => void
  ): Unsubscribe {
    const q = query(
      collection(db, 'content-creation-submissions'),
      where('submittedBy', '==', userId),
      orderBy('submittedAt', 'desc')
    );

    return onSnapshot(q, (querySnapshot) => {
      const submissions = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        submittedAt: doc.data().submittedAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        reviewedAt: doc.data().reviewedAt?.toDate(),
      })) as ContentCreationSubmission[];
      
      callback(submissions);
    });
  }

  // Real-time listener for all submissions (for admins)
  listenToAllSubmissions(
    callback: (submissions: ContentCreationSubmission[]) => void
  ): Unsubscribe {
    const q = query(
      collection(db, 'content-creation-submissions'),
      orderBy('submittedAt', 'desc')
    );

    return onSnapshot(q, (querySnapshot) => {
      const submissions = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        submittedAt: doc.data().submittedAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        reviewedAt: doc.data().reviewedAt?.toDate(),
      })) as ContentCreationSubmission[];
      
      callback(submissions);
    });
  }

  // Review content (approve, deny, request rework)
  async reviewContent(
    submissionId: string,
    reviewAction: ContentReviewAction,
    reviewerId: string,
    reviewerName: string
  ): Promise<void> {
    try {
      // First, get the submission to access submitter details
      const submissionRef = doc(db, 'content-creation-submissions', submissionId);
      const submissionDoc = await getDocs(query(
        collection(db, 'content-creation-submissions'),
        where('__name__', '==', submissionId)
      ));
      
      if (submissionDoc.empty) {
        throw new Error('Submission not found');
      }
      
      const submissionData = submissionDoc.docs[0].data() as ContentCreationSubmission;

      const updateData: Partial<ContentCreationSubmission> = {
        status: this.mapActionToStatus(reviewAction.action),
        reviewedBy: reviewerId,
        reviewedByName: reviewerName,
        reviewedAt: new Date(),
        updatedAt: new Date(),
      };

      // Add specific feedback based on action
      switch (reviewAction.action) {
        case 'approve':
          updateData.approvalNotes = reviewAction.approvalNotes;
          break;
        case 'deny':
          updateData.rejectionReason = reviewAction.rejectionReason;
          updateData.feedback = reviewAction.feedback;
          break;
        case 'request_rework':
          updateData.feedback = reviewAction.feedback;
          updateData.reworkRequested = true;
          break;
      }

      await updateDoc(submissionRef, {
        ...updateData,
        reviewedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // 🚀 NEW: Auto-add approved content to content calendar
      if (reviewAction.action === 'approve') {
        try {
          await this.addApprovedContentToCalendar(submissionData, reviewerId, reviewerName, submissionId);
          console.log(`✅ Approved content "${submissionData.title}" added to content calendar`);
          
          // Additional notification for successful calendar addition
          try {
                         await notificationService.createNotification(
               submissionData.submittedBy,
               'ContentAutoAddedToCalendar' as any,
               '📅 Content Added to Calendar',
               `Your approved content "${submissionData.title}" has been automatically added to the content calendar`,
               {
                 contentId: submissionId,
                 contentTitle: submissionData.title,
                 platforms: submissionData.platform,
                 reviewedBy: reviewerId,
                 reviewedByName: reviewerName
               },
               '/marketing/content-calendar',
               'View in Calendar'
             );
          } catch (notificationError) {
            console.error('Failed to send calendar addition notification:', notificationError);
            // Don't fail if notification fails
          }
        } catch (calendarError) {
          console.error('Failed to add approved content to calendar:', calendarError);
          // Don't fail the review if calendar addition fails
        }
      }

      // Send notification to the submitter
      try {
        // Map review action to notification action format
        let notificationAction: 'approved' | 'denied' | 'rework_requested';
        switch (reviewAction.action) {
          case 'approve': notificationAction = 'approved'; break;
          case 'deny': notificationAction = 'denied'; break;
          case 'request_rework': notificationAction = 'rework_requested'; break;
        }
        
        await notificationService.notifyContentReviewed(
          submissionId,
          submissionData.title,
          submissionData.submittedBy,
          submissionData.submittedByName,
          reviewerId,
          reviewerName,
          notificationAction,
          reviewAction.feedback
        );
      } catch (notificationError) {
        console.error('Failed to send content review notifications:', notificationError);
        // Don't fail the review if notifications fail
      }
    } catch (error) {
      console.error('Error reviewing content:', error);
      throw error;
    }
  }

  // Map review action to status
  private mapActionToStatus(action: ContentReviewAction['action']): ContentApprovalStatus {
    switch (action) {
      case 'approve': return 'approved';
      case 'deny': return 'denied';
      case 'request_rework': return 'rework_requested';
      default: return 'submitted';
    }
  }

  // Add approved content to content calendar
  private async addApprovedContentToCalendar(
    submissionData: ContentCreationSubmission,
    reviewerId: string,
    reviewerName: string,
    submissionId?: string
  ): Promise<void> {
    try {
      // Transform content creation submission to content calendar format
      const primaryPlatform = submissionData.platform[0] || 'instagram';
      const now = new Date();
      
      // Create content calendar entry
      const calendarContent = {
        type: this.mapMediaTypeToCalendarType(submissionData.mediaType),
        title: submissionData.title,
        caption: submissionData.caption,
        platform: primaryPlatform.toLowerCase(),
        files: submissionData.mediaFiles || [],
        thumbnails: submissionData.thumbnailUrl ? [submissionData.thumbnailUrl] : undefined,
        scheduledDate: new Date(now.getTime() + (24 * 60 * 60 * 1000)), // Default to tomorrow
        status: 'approved',
        tags: [
          'auto-approved',
          ...submissionData.platform.map(p => p.toLowerCase()),
          ...(submissionData.promotedItems || [])
        ],
        assignedTo: submissionData.submittedBy,
        createdBy: submissionData.submittedBy,
        createdAt: submissionData.submittedAt,
        updatedAt: now,
        priority: 'medium',
        estimatedReach: undefined,
        engagementGoal: undefined,
        comments: [
          {
            id: Date.now().toString(),
            userId: reviewerId,
            userName: reviewerName,
            message: `✅ Auto-added from approved content creation submission: "${submissionData.title}"${submissionData.approvalNotes ? `\n\nApproval Notes: ${submissionData.approvalNotes}` : ''}`,
            timestamp: now,
            type: 'approval'
          }
        ],
        activityLog: [
          {
            id: Date.now().toString(),
            userId: reviewerId,
            userName: reviewerName,
            action: 'content_approved_and_added',
            details: `Content "${submissionData.title}" approved and automatically added to content calendar for ${primaryPlatform}`,
            timestamp: now,
            contentId: '' // Will be set after document creation
          }
        ],
                 // Track source from content creation
         sourceSubmissionId: submissionId,
         sourceSubmittedBy: submissionData.submittedBy,
         sourceSubmittedByName: submissionData.submittedByName,
         sourceSubmittedAt: submissionData.submittedAt
      };

      // Add to content calendar collection
      const docRef = await addDoc(collection(db, 'content-calendar'), {
        ...calendarContent,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        scheduledDate: Timestamp.fromDate(calendarContent.scheduledDate)
      });

      // Update activity log with the actual content ID
      await updateDoc(docRef, {
        activityLog: [
          {
            ...calendarContent.activityLog[0],
            contentId: docRef.id
          }
        ]
      });

      console.log(`✅ Added approved content to calendar with ID: ${docRef.id}`);
      
    } catch (error) {
      console.error('Error adding approved content to calendar:', error);
      throw error;
    }
  }

  // Map media type from content creation to content calendar
  private mapMediaTypeToCalendarType(mediaType: string): 'image' | 'video' | 'carousel' | 'infographic' | 'blog' {
    switch (mediaType) {
      case 'image': return 'image';
      case 'video': return 'video';
      case 'carousel': return 'carousel';
      default: return 'image';
    }
  }

  // Resubmit content after rework
  async resubmitContent(
    originalSubmissionId: string,
    formData: ContentCreationFormData,
    userId: string,
    userName: string,
    userEmail: string
  ): Promise<string> {
    try {
      // Upload new media files
      const mediaUrls = await this.uploadMediaFiles(formData.mediaFiles, userId);
      const mediaType = this.determineMediaType(formData.mediaFiles);

      // Create resubmission data
      const resubmissionData: Omit<ContentCreationSubmission, 'id'> = {
        submittedBy: userId,
        submittedByName: userName,
        submittedByEmail: userEmail,
        title: formData.title.trim(),
        caption: formData.caption.trim(),
        platform: formData.platform,
        mediaFiles: mediaUrls,
        mediaType,
        promotedItems: formData.promotedItems ? [formData.promotedItems.trim()] : undefined,
        status: 'resubmitted',
        submittedAt: new Date(),
        updatedAt: new Date(),
        originalSubmissionId,
      };

      // Add resubmission to Firestore
      const docRef = await addDoc(collection(db, 'content-creation-submissions'), {
        ...resubmissionData,
        submittedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      return docRef.id;
    } catch (error) {
      console.error('Error resubmitting content:', error);
      throw error;
    }
  }

  // Get submissions by status
  async getSubmissionsByStatus(status: ContentApprovalStatus): Promise<ContentCreationSubmission[]> {
    try {
      const q = query(
        collection(db, 'content-creation-submissions'),
        where('status', '==', status),
        orderBy('submittedAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        submittedAt: doc.data().submittedAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        reviewedAt: doc.data().reviewedAt?.toDate(),
      })) as ContentCreationSubmission[];
    } catch (error) {
      console.error('Error fetching submissions by status:', error);
      throw error;
    }
  }

  // Get count of pending content (for sidebar notifications)
  async getPendingContentCount(): Promise<number> {
    try {
      const statuses = ['submitted', 'under_review', 'resubmitted'];
      let totalCount = 0;

      for (const status of statuses) {
        const q = query(
          collection(db, 'content-creation-submissions'),
          where('status', '==', status)
        );
        const querySnapshot = await getDocs(q);
        totalCount += querySnapshot.size;
      }

      return totalCount;
    } catch (error) {
      console.error('Error fetching pending content count:', error);
      return 0;
    }
  }

  // Get count of submitted content (for sidebar notifications)
  async getSubmittedContentCount(): Promise<number> {
    try {
      const q = query(
        collection(db, 'content-creation-submissions'),
        where('status', '==', 'submitted')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.size;
    } catch (error) {
      console.error('Error fetching submitted content count:', error);
      return 0;
    }
  }

  // Real-time listener for pending content count
  listenToPendingContentCount(
    callback: (count: number) => void
  ): Unsubscribe {
    // Listen to submitted status
    const submittedQ = query(
      collection(db, 'content-creation-submissions'),
      where('status', '==', 'submitted')
    );

    const underReviewQ = query(
      collection(db, 'content-creation-submissions'),
      where('status', '==', 'under_review')
    );

    const resubmittedQ = query(
      collection(db, 'content-creation-submissions'),
      where('status', '==', 'resubmitted')
    );

    let submittedCount = 0;
    let underReviewCount = 0;
    let resubmittedCount = 0;

    const updateTotalCount = () => {
      callback(submittedCount + underReviewCount + resubmittedCount);
    };

    const unsubscribe1 = onSnapshot(submittedQ, (snapshot) => {
      submittedCount = snapshot.size;
      updateTotalCount();
    });

    const unsubscribe2 = onSnapshot(underReviewQ, (snapshot) => {
      underReviewCount = snapshot.size;
      updateTotalCount();
    });

    const unsubscribe3 = onSnapshot(resubmittedQ, (snapshot) => {
      resubmittedCount = snapshot.size;
      updateTotalCount();
    });

    // Return a function that unsubscribes from all listeners
    return () => {
      unsubscribe1();
      unsubscribe2();
      unsubscribe3();
    };
  }

  // Real-time listener for submitted content count only
  listenToSubmittedContentCount(
    callback: (count: number) => void
  ): Unsubscribe {
    const submittedQ = query(
      collection(db, 'content-creation-submissions'),
      where('status', '==', 'submitted')
    );

    return onSnapshot(submittedQ, (snapshot) => {
      callback(snapshot.size);
    });
  }

  // Update content thumbnail
  async updateContentThumbnail(submissionId: string, thumbnailFile: File, userId: string): Promise<void> {
    try {
      // Get the current submission data
      const submissionRef = doc(db, 'content-creation-submissions', submissionId);
      const submissionDoc = await getDocs(query(
        collection(db, 'content-creation-submissions'),
        where('__name__', '==', submissionId)
      ));
      
      if (submissionDoc.empty) {
        throw new Error('Submission not found');
      }
      
      const submissionData = submissionDoc.docs[0].data() as ContentCreationSubmission;
      
      // Upload the new thumbnail
      const newThumbnailUrl = await this.uploadThumbnailFile(thumbnailFile, userId);
      
      // Delete the old thumbnail if it exists and is not one of the media files
      if (submissionData.thumbnailUrl && 
          !submissionData.mediaFiles.includes(submissionData.thumbnailUrl)) {
        try {
          const oldThumbnailPath = this.extractStoragePathFromUrl(submissionData.thumbnailUrl);
          if (oldThumbnailPath) {
            const oldThumbnailRef = ref(storage, oldThumbnailPath);
            await deleteObject(oldThumbnailRef);
          }
        } catch (error) {
          console.error('Error deleting old thumbnail:', error);
          // Don't fail the entire operation if old thumbnail deletion fails
        }
      }
      
      // Update the submission with the new thumbnail URL
      await updateDoc(submissionRef, {
        thumbnailUrl: newThumbnailUrl,
        updatedAt: serverTimestamp(),
      });
      
    } catch (error: any) {
      console.error('Error updating content thumbnail:', error);
      
      // Enhanced error handling
      if (error.code === 'permission-denied') {
        throw new Error('Permission denied. You do not have permission to update this content.');
      } else if (error.code === 'not-found') {
        throw new Error('Content not found. It may have been deleted.');
      } else if (error.code === 'unavailable') {
        throw new Error('Database is temporarily unavailable. Please try again later.');
      } else if (error.message?.includes('Invalid thumbnail') || error.message?.includes('exceeds')) {
        throw error; // Re-throw file validation errors as-is
      } else {
        throw new Error(`Failed to update thumbnail: ${error.message || 'Unknown error'}`);
      }
    }
  }

  // Delete content submission
  async deleteContent(submissionId: string): Promise<void> {
    try {
      // Get submission data first to clean up associated files
      const submissionDoc = doc(db, 'content-creation-submissions', submissionId);
      const submissionSnapshot = await getDocs(query(
        collection(db, 'content-creation-submissions'),
        where('__name__', '==', submissionId)
      ));

      if (!submissionSnapshot.empty) {
        const submissionData = submissionSnapshot.docs[0].data() as ContentCreationSubmission;
        
        // Delete associated media files from storage
        if (submissionData.mediaFiles && submissionData.mediaFiles.length > 0) {
          await Promise.all(submissionData.mediaFiles.map(async (mediaUrl) => {
            try {
              // Extract storage path from URL
              const storagePath = this.extractStoragePathFromUrl(mediaUrl);
              if (storagePath) {
                const storageRef = ref(storage, storagePath);
                await deleteObject(storageRef);
              }
            } catch (error) {
              console.error('Error deleting media file:', error);
              // Don't fail the entire deletion if one file fails
            }
          }));
        }

        // Delete thumbnail file if it exists and is different from media files
        if (submissionData.thumbnailUrl && 
            !submissionData.mediaFiles.includes(submissionData.thumbnailUrl)) {
          try {
            const thumbnailPath = this.extractStoragePathFromUrl(submissionData.thumbnailUrl);
            if (thumbnailPath) {
              const thumbnailRef = ref(storage, thumbnailPath);
              await deleteObject(thumbnailRef);
            }
          } catch (error) {
            console.error('Error deleting thumbnail file:', error);
            // Don't fail the entire deletion if thumbnail deletion fails
          }
        }
      }

      // Delete the document from Firestore
      await deleteDoc(submissionDoc);
      
    } catch (error: any) {
      console.error('Error deleting content:', error);
      
      // Enhanced error handling
      if (error.code === 'permission-denied') {
        throw new Error('Permission denied. You do not have permission to delete this content.');
      } else if (error.code === 'not-found') {
        throw new Error('Content not found. It may have already been deleted.');
      } else if (error.code === 'unavailable') {
        throw new Error('Database is temporarily unavailable. Please try again later.');
      } else {
        throw new Error(`Failed to delete content: ${error.message || 'Unknown error'}`);
      }
    }
  }

  // Helper method to extract storage path from Firebase Storage URL
  private extractStoragePathFromUrl(url: string): string | null {
    try {
      // Firebase Storage URL format: https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{path}?alt=media&token={token}
      const urlParts = url.split('/o/');
      if (urlParts.length === 2) {
        const pathPart = urlParts[1].split('?')[0];
        return decodeURIComponent(pathPart);
      }
      return null;
    } catch (error) {
      console.error('Error extracting storage path from URL:', error);
      return null;
    }
  }
}

export const contentCreationService = ContentCreationService.getInstance(); 